import React from 'react';
import _ from 'lodash';

import {
  BulkReceiveReturnAuthorizationsModal,
  activityTemplates,
  routeDirectory,
  selectFields,
  useTabSearchParams,
  BulkReceiveSpecificReturnAuthorizationsModal
} from 'modules';
import {
  Box,
  Button,
  TextField
} from '@mui/material';
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import { modalUtil } from 'iron-forge-react-modules';
import * as LookUpTier1ReturnAuthorizationModal from './LookUpTier1ReturnAuthorizationModal/LookUpTier1ReturnAuthorizationModal';
import * as ReceiveUnknownSerialNumberWizard from './ReceiveUnknownSerialNumberWizard/ReceiveUnknownSerialNumberWizard';
import * as PrintLabelTable from './PrintLabelTable/PrintLabelTable';
import * as TabBar from './TabBar/TabBar';
import * as Table from './Table/Table';
import * as ReturnAuthorizationGroupTable from './ReturnAuthorizationGroupTable/ReturnAuthorizationGroupTable';
import * as shared from './shared';



const { FC: StoreSelectField } = selectFields.StoreSelectField.genComponentAndControllerGenerator({
  types: ['cleoTier1']
});



export const Tier1ReturnAuthorizationsActivity: React.FC = () => {
  const navigate = useNavigate();
  const {
    modalType2IsOpen,
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'lookUpReturnAuthorization',
    'bulkReceive',
    'receiveUnknownSerialNumber'
  ]);
  const params = shared.useReturnAuthorizationActivityParams();
  const [tabId, setTabId] = useTabSearchParams({
    defaultTabId: '',
    paramName: 'storeId'
  });

  const storeId = tabId ?? undefined;



  const handleClickViewAll = React.useCallback(
    () => {
      navigate(routeDirectory.admin.tier1ReturnAuthorizations.all.path());
    },
    [navigate]
  );

  const handleSelectReturnAuthorization = React.useCallback(
    (returnAuthorizationId: string) => {
      setModalIsOpen('lookUpReturnAuthorization', false);
      navigate(routeDirectory.admin.tier1ReturnAuthorizations.detail.path(returnAuthorizationId));
    },
    [navigate, setModalIsOpen]
  );

  const [search, setSearch] = useSearchParams();
  const debouncedProcessSubmit = React.useMemo(
    () => _.debounce((value: string) => {
      setSearch(val => {
        val.set('search', value);
        return val;
      });
    }, 1000),
    [setSearch]
  );

  const searchText = React.useMemo(() => search.get('search') ?? '', [search]);

  return (
    <>
      <activityTemplates.AdminQueuingActivityTemplate.FC
        elButtons={[]}
        elTabBar={(
          <TabBar.FC
            storeId={storeId}
            searchText={searchText} />
        )}
        elTabBarActions={(
          <>
            <TextField
              defaultValue={searchText}
              onChange={ev => debouncedProcessSubmit(ev.target.value)} />

            <Box sx={{ minWidth: 300 }}>
              <StoreSelectField
                disableDefaultMargins
                label="Customer"
                selectProps={{
                  type: 'singleSelect',
                  value: { id: storeId ?? '', displayName: '' },
                  onChange: store => setTabId(store?.id ?? '')
                }} />
            </Box>

            <Box>
              <Button
                color="info"
                variant="outlined"
                onClick={handleClickViewAll}>
                View All
              </Button>
            </Box>
          </>
        )}>

        {params.tabId === 'preAlertAcknowledged' && (
          <ReturnAuthorizationGroupTable.FC
            filter={{
              storeId,
              searchText
            }} />
        )}

        {params.tabId === 'awaitingTriageObservation' && (
          <Table.FC
            filter={{
              storeId: storeId,
              inState: 'awaitingTriageObservation',
              searchText
            }}
            columnIdsToRender={[
              'requestedDate',
              'itemReceiptDate',
              'client',
              'referenceNumber',
              'nsRmaRecord',
              'productSku',
              'productName',
              'serialNumber',
              'goTo'
            ]} />
        )}

        {params.tabId === 'qualityControl' && (
          <Table.FC
            filter={{
              storeId,
              inState: 'awaitingQualityControl',
              searchText
            }}
            columnIdsToRender={[
              'requestedDate',
              'itemReceiptDate',
              'client',
              'referenceNumber',
              'nsRmaRecord',
              'productSku',
              'productName',
              'serialNumber',
              'qualityControl',
              'goTo'
            ]} />
        )}

        {params.tabId === 'awaitingReturnDecision' && (
          <Table.FC
            filter={{
              storeId: storeId,
              inState: 'awaitingTriage',
              searchText
            }}
            columnIdsToRender={[
              'requestedDate',
              'itemReceiptDate',
              'client',
              'nsRmaRecord',
              'observedIssue',
              'productSku',
              'productName',
              'serialNumber',
              'goTo'
            ]} />
        )}

        {params.tabId === 'printLabel' && (
          <PrintLabelTable.FC storeId={storeId} />
        )}
      </activityTemplates.AdminQueuingActivityTemplate.FC>

      {modalType2IsOpen.lookUpReturnAuthorization && (
        <LookUpTier1ReturnAuthorizationModal.FC
          onClose={() => setModalIsOpen('lookUpReturnAuthorization', false)}
          onClickReceiveUnknownSerialNumber={serialNumber => {
            setModalIsOpen('lookUpReturnAuthorization', false);
            setModalIsOpen('receiveUnknownSerialNumber', serialNumber);
          }}
          onSelect={handleSelectReturnAuthorization} />
      )}

      {modalType2IsOpen.receiveUnknownSerialNumber && (
        <ReceiveUnknownSerialNumberWizard.FC
          serialNumber={modalType2TargetId.receiveUnknownSerialNumber}
          onClose={() => setModalIsOpen('receiveUnknownSerialNumber', false)} />
      )}

      {modalType2IsOpen.bulkReceive && (
        <BulkReceiveReturnAuthorizationsModal.FC
          onClose={() => setModalIsOpen('bulkReceive', false)} />
      )}

      <BulkReceiveSpecificReturnAuthorizationsModal.FC />
    </>
  );
};
